import axios from 'axios';
import { API_Base } from './config';

// Function to get cookie by name, with special handling for csrftoken
function getCookie(name) {
  // Look for the specific cookie name you set in Django settings
  const cookieName = name === 'csrftoken' ? 'lingua_csrftoken' : name;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${cookieName}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

// Create axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: API_Base,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable sending cookies with cross-origin requests - crucial for session auth
  withCredentials: true,
});

// Add request interceptor for common handling
axiosInstance.interceptors.request.use(
  (config) => {
    // Add CSRF token to non-GET requests
    if (config.method !== 'get') {
      const csrfToken = getCookie('csrftoken');
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }
    
    // Add authorization header if user is logged in
    // First try to get auth token from sessionStorage (more reliable for API requests)
    const accessToken = sessionStorage.getItem('access_token');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
      // Fall back to localStorage if not in sessionStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          // Check all possible token locations
          const token = userData.access_token || userData.access;
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
            // Also sync to sessionStorage for future requests
            sessionStorage.setItem('access_token', token);
          }
        } catch (e) {
          // Invalid stored user data
          localStorage.removeItem('user');
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle session expiry - 401 Unauthorized or 403 Forbidden
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Clear all session data
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('user_group');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_id');
      
      // Only redirect if not already on login page and if we're in a browser environment
      if (typeof window !== 'undefined' && 
          window.location.pathname !== '/login' && 
          window.location.pathname !== '/sign-up' && 
          window.location.pathname !== '/') {
        // Save current location for redirect after login
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;